import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import { t } from "@impero-com/common/utils/i18n";
import { set, clone } from "@impero-com/common/utils/obj";
import memoizeOne from "memoize-one";

import {
    UpdatableControlPrograms,
    Presence,
    ControlProgramTagViewListDto as ControlProgramTags,
} from "api/generated/model";
import { MultiTextArea } from "components/form/legacyFields/multiTextArea";
import { TagsSelector, TagsSelectorValues } from "components/tag/selector";
import { FormRootComponentProps } from "utils/form";
import { Guid } from "utils/guid";

import { adaptTags } from "./utils";

interface ChangeDetailsFormValues {
    descriptions: Array<string>;
    tags: TagsSelectorValues;
    patch: Pick<UpdatableControlPrograms, "description" | "tags">;
}

interface ChangeDetailsFormProps extends FormRootComponentProps<ChangeDetailsFormValues> {
    readOnly: boolean;
    controlProgramTags: ControlProgramTags;
    initialDescriptions: Array<string>;
    initialTags: Array<Set<Guid>>;
}

class ChangeDetailsForm extends React.PureComponent<ChangeDetailsFormProps> {
    private adaptAllTags = memoizeOne(adaptTags);

    render = () => {
        const {
            form,
            readOnly,
            controlProgramTags,
            initialDescriptions,
            initialTags,
        } = this.props;
        const allTags = this.adaptAllTags(
            controlProgramTags.content.map((tag) => tag.resource)
        );
        return (
            <>
                <Form.Item>
                    {form.getFieldDecorator("descriptions", {
                        trigger: "", // Update handled by calls to setFieldsValue
                        valuePropName: "values",
                        initialValue: initialDescriptions,
                    })(
                        !readOnly ? (
                            <MultiTextArea
                                type="EDIT"
                                name="description"
                                label={t("ControlProgram_Description")}
                                onChange={(description: string) => {
                                    form.setFieldsValue({
                                        descriptions: Array<string>(
                                            initialDescriptions.length
                                        ).fill(description),
                                        patch: set(
                                            form.getFieldValue("patch"),
                                            { description }
                                        ),
                                    });
                                }}
                            />
                        ) : (
                            <MultiTextArea
                                type="READ_ONLY"
                                name="description"
                                label={t("ControlProgram_Description")}
                            />
                        )
                    )}
                </Form.Item>
                <Form.Item>
                    {form.getFieldDecorator("tags", {
                        trigger: "", // Update handled by calls to setFieldsValue
                        valuePropName: "values",
                        initialValue: {
                            tags: initialTags,
                            absentTags: new Set(),
                        },
                    })(
                        !readOnly ? (
                            <TagsSelector
                                type="EDIT"
                                label={t(
                                    "EditControlProgram_ControlProgramTags"
                                )}
                                allTagCategories={
                                    controlProgramTags.linkedResources
                                }
                                allTags={allTags}
                                onAddTag={this.handleAddTag}
                                onRemoveTag={this.handleRemoveTag}
                            />
                        ) : (
                            <TagsSelector
                                type="READ_ONLY"
                                label={t(
                                    "EditControlProgram_ControlProgramTags"
                                )}
                                allTagCategories={
                                    controlProgramTags.linkedResources
                                }
                                allTags={allTags}
                            />
                        )
                    )}
                </Form.Item>
                <Form.Item>
                    {
                        // This is only used to set the initial patch value.
                        // It ensures it is always defined.
                        form.getFieldDecorator("patch", {
                            trigger: "", // Update handled by calls to setFieldsValue
                            initialValue: {},
                        })(<></>)
                    }
                </Form.Item>
            </>
        );
    };

    private handleAddTag = (tagId: Guid) => {
        const { form } = this.props;
        const values = form.getFieldValue("tags") || { tags: [] };
        const patch = form.getFieldValue("patch") || {};
        const newTags = values.tags.map((tagIds) => {
            const newTagIds = new Set(tagIds);
            newTagIds.add(tagId);
            return newTagIds;
        });
        const absentTags = values.absentTags || [];
        const newAbsentTags = new Set(absentTags);
        newAbsentTags.delete(tagId);
        const newPatch: Record<string, Presence> = patch.tags
            ? clone(patch.tags)
            : {};
        newPatch[tagId] = "Present";
        form.setFieldsValue({
            tags: {
                tags: newTags,
                absentTags: newAbsentTags,
            },
            patch: set(patch, { tags: newPatch }),
        });
    };

    private handleRemoveTag = (tagId: Guid) => {
        const { form } = this.props;
        const values = form.getFieldValue("tags") || { tags: [] };
        const patch = form.getFieldValue("patch") || {};
        const newTags = values.tags.map((tagIds) => {
            const newTagIds = new Set(tagIds);
            newTagIds.delete(tagId);
            return newTagIds;
        });
        const absentTags = values.absentTags || [];
        const newAbsentTags = new Set(absentTags);
        newAbsentTags.add(tagId);
        const newPatch: Record<string, Presence> = patch.tags
            ? clone(patch.tags)
            : {};
        newPatch[tagId] = "Absent";
        form.setFieldsValue({
            tags: {
                tags: newTags,
                absentTags: newAbsentTags,
            },
            patch: set(patch, { tags: newPatch }),
        });
    };
}

export { ChangeDetailsForm, type ChangeDetailsFormValues };
